import { useCallback, useEffect, useMemo, useState } from "react";
import type { SourceRef } from "../contract/types";
import {
  API_LOCATION,
  getCourseOutline,
  getSources,
  type CourseOutline,
  type SourceData,
} from "../api/client";
import { FALLBACK_OUTLINE, FALLBACK_SOURCES } from "../course/outline";
import { useRun } from "../stream/useRun";
import type { Turn } from "../stream/reducer";
import { AppShell } from "./AppShell";
import { CourseNav } from "./CourseNav";
import { StreamView } from "./StreamView";
import { StageView } from "./StageView";
import { SourceRail } from "./SourceRail";
import { clearSiteData } from "../lib/siteData";
import { IntroTour } from "./IntroTour";
import { Icon } from "../components/Icon";

/**
 * Espacio de trabajo: pega el stream, la navegación y el rail en el layout de
 * tres columnas y resuelve el estado que comparten.
 *
 * Cuando hay un preset activo, el centro pasa a ser la vista de pantalla
 * completa; el stream sigue vivo detrás —el run no se interrumpe— y al volver
 * el estudiante encuentra la conversación donde la dejó.
 */

/**
 * Fuentes del turno: las que vienen en un `source_list` más las rutas sueltas
 * de `source_refs`, resueltas contra el índice del bundle cuando se puede.
 * Sin índice, la ruta se muestra tal cual: es información, aunque sea escueta.
 */
function collectSources(turn: Turn | undefined, index: SourceData[]): SourceRef[] {
  if (!turn) return [];
  const byPath = new Map<string, SourceRef>();

  for (const block of turn.blocks) {
    if (block.type === "source_list") {
      for (const source of block.sources ?? []) {
        if (!byPath.has(source.path)) byPath.set(source.path, source);
      }
    }
  }

  const known = new Map(index.map((source) => [source.path, source]));
  for (const block of turn.blocks) {
    const refs = "source_refs" in block ? block.source_refs : undefined;
    if (!Array.isArray(refs)) continue;
    for (const path of refs) {
      if (byPath.has(path)) continue;
      const match = known.get(path);
      byPath.set(
        path,
        match
          ? {
              path: match.path,
              title: match.title ?? null,
              kind: match.kind ?? "course",
              snippet: match.snippet ?? null,
              trust: match.trust ?? null,
              stale: match.stale ?? null,
              page: match.page ?? null,
            }
          : { path, title: null, kind: "course" },
      );
    }
  }

  return [...byPath.values()];
}

function ConnectionBadge({
  boot,
  connection,
  isStreaming,
}: {
  boot: "loading" | "ready" | "offline";
  connection: string;
  isStreaming: boolean;
}) {
  const tone = boot === "offline" ? "offline" : isStreaming ? "busy" : "online";
  const label =
    boot === "loading"
      ? "Conectando"
      : boot === "offline"
        ? "Sin backend"
        : connection === "retrying"
          ? "Reconectando"
          : isStreaming
            ? "En curso"
            : "Listo";

  return (
    <span className={`conn conn--${tone}`} role="status">
      <span className="conn__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function Workspace() {
  const run = useRun();
  const [outline, setOutline] = useState<CourseOutline>(FALLBACK_OUTLINE);
  const [bundle, setBundle] = useState<SourceData[]>([]);
  const [bundleTotal, setBundleTotal] = useState<number | null>(null);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [draft, setDraft] = useState("");
  const [activeModule, setActiveModule] = useState<string | null>(null);

  useEffect(() => {
    if (run.boot === "loading") return;
    if (run.boot === "offline") {
      // Sin backend, el rail sigue teniendo sentido con el índice del sílabo:
      // el estudiante ve de qué material dispone aunque no pueda preguntar.
      setBundle(FALLBACK_SOURCES);
      setSourcesLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const [nextOutline, nextSources] = await Promise.all([
        getCourseOutline(),
        getSources(24),
      ]);
      if (cancelled) return;
      if (nextOutline) setOutline(nextOutline);
      setBundle(nextSources.sources.length > 0 ? nextSources.sources : FALLBACK_SOURCES);
      setBundleTotal(nextSources.sources.length > 0 ? nextSources.total : null);
      setSourcesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [run.boot]);

  const lastAssistant = useMemo(
    () => [...run.turns].reverse().find((turn) => turn.role === "assistant"),
    [run.turns],
  );

  const cited = useMemo(() => collectSources(lastAssistant, bundle), [lastAssistant, bundle]);

  const selectedTopic = outline.units.flatMap((unit) => unit.modules)
    .find((module) => module.module === activeModule);

  const send = useCallback(
    (content: string) => {
      if (run.boot !== "ready" || run.isStreaming || !content.trim()) return;
      const message = selectedTopic
        ? `@M${selectedTopic.module.replace(/^M/, "")} · ${selectedTopic.title}\n${content.trim()}`
        : content.trim();
      setDraft("");
      run.closePreset();
      void run.send(message);
    },
    [run, selectedTopic],
  );

  const emit = useCallback(
    (blockId: string, event: string, payload: Record<string, unknown>) => {
      if (event === "action.invoke" && payload.action_event === "study.expand" && typeof payload.label === "string") {
        send(payload.label);
        return;
      }
      if (run.boot === "ready" && !run.isStreaming) void run.invokeAction(blockId, event, payload);
    },
    [run, send],
  );

  const startNewSession = useCallback(async () => {
    if (resetting) return;
    setResetting(true);
    run.stop();
    await clearSiteData();
    window.location.reload();
  }, [resetting, run]);

  const handleSelectModule = useCallback((module: string) => {
    setActiveModule((current) => current === module ? null : module);
    run.closePreset();
  }, [run.closePreset]);

  return (
    <AppShell
      stageMode={run.preset !== null}
      status={<ConnectionBadge boot={run.boot} connection={run.connection} isStreaming={run.isStreaming} />}
      nav={
        <CourseNav
          outline={outline}
          activeModule={activeModule}
          onSelectModule={handleSelectModule}
          onNewSession={() => { void startNewSession(); }}
          resetting={resetting || run.boot === "loading"}
        />
      }
      rail={
        <SourceRail
          cited={cited}
          index={bundle}
          total={bundleTotal}
          loading={sourcesLoading}
        />
      }
      main={
        <div className="workspace__main">
          <IntroTour />
          <BackendNotice boot={run.boot} onRetry={run.retryBoot} />
          {run.preset ? (
            <StageView
              preset={run.preset}
              onClose={run.closePreset}
              emit={emit}
              interactive={run.boot === "ready"}
            />
          ) : (
            <StreamView
              turns={run.turns}
              isStreaming={run.isStreaming}
              connection={run.connection}
              error={run.boot === "offline" ? null : run.lastError}
              emit={emit}
              interactive={run.boot === "ready"}
              onSend={send}
              onStop={run.stop}
              onOpenPreset={run.openPreset}
              draft={draft}
              onDraftChange={setDraft}
              topic={selectedTopic?.title ?? null}
              onClearTopic={() => setActiveModule(null)}
            />
          )}
        </div>
      }
    />
  );
}

/** Aviso de arranque cuando el backend no responde: la UI no finge estar viva. */
export function BackendNotice({ boot, onRetry }: { boot: "loading" | "ready" | "offline"; onRetry: () => void }) {
  if (boot !== "offline") return null;
  return (
    <div className="notice" role="alert">
      <Icon name="alert" size="1rem" />
      <span>
        El backend del agente no responde en{" "}
        <code className="u-mono">{API_LOCATION}</code>. La navegación del curso sigue
        disponible, pero no se pueden enviar mensajes.
      </span>
      <button type="button" className="u-button u-button--small" onClick={onRetry}>
        Reintentar
      </button>
    </div>
  );
}

export default Workspace;
